import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Loader2, Save, Plus, X, RotateCcw,
} from "lucide-react";

const SECTIONS = [
  { key: "business_basics",   label: "Business basics",    intro: "Tell DMS about your dive business so every recommendation lands." },
  { key: "goals",             label: "Goals",              intro: "What do you want the next 90 days to achieve?" },
  { key: "offers",            label: "Offers",             intro: "Add every course, trip, product or service you actively sell." },
  { key: "audience_segments", label: "Audience",           intro: "Who exactly are you speaking to? Pick suggested segments or add your own." },
  { key: "channels",          label: "Channels",           intro: "Which channels do you actually use? DMS will build strategy around only these." },
  { key: "brand_voice",       label: "Brand voice",        intro: "How your dive brand sounds — so DMS never writes off-brand." },
  { key: "assets",            label: "Assets",             intro: "What content raw material do you have? This decides whether we create finished posts or shot lists." },
  { key: "constraints",       label: "Constraints",        intro: "How much time you have + hard rules DMS must respect." },
];

const CERT_AGENCIES = ["PADI","SSI","RAID","SDI","TDI","IANTD","CMAS","NAUI","Other","None"];
const BUSINESS_TYPES = ["Dive centre","Dive resort","Liveaboard","Dive shop / gear","Freelance instructor","Dive guide","Dive creator","Marketing agency","Other"];
const CTA_METHODS = ["WhatsApp","Email","Booking form","Website","Phone call","DM"];
const GOAL_OPTIONS = [
  "Generate beginner course bookings","Promote Discover Scuba / Try Dive experiences","Sell guided dives",
  "Fill advanced or specialty courses","Promote technical diving","Sell IDC or professional training",
  "Sell dive equipment","Promote dive gear servicing","Grow newsletter subscribers","Build creator audience",
  "Launch or sell a digital product","Increase WhatsApp enquiries","Improve SEO/blog traffic",
  "Improve Google Business Profile visibility","Promote trips/liveaboards","Build local community",
  "Reactivate past customers","Promote a seasonal campaign","Build authority before high season",
];
const OFFER_TYPES = ["Course","Guided dive","Dive trip","Equipment product","Equipment service","Digital product","Coaching/consulting","Creator offer","Event/workshop","Other"];
const OFFER_PRIORITIES = ["Priority","Supporting","Evergreen","Seasonal","Trust-building"];
const AUDIENCE_SUGGESTIONS = {
  "dive_centre": ["Nervous beginner","Holiday try-diver","Couple looking for an activity","Newly certified diver","Rusty diver","Advanced diver","Wreck diver","Tec-curious diver","Local weekend diver","Parent booking for teenager","Instructor candidate","Returning customer"],
  "gear_shop":   ["New diver buying first kit","Experienced diver upgrading","Technical diver","Underwater photographer","Drysuit diver","Travelling diver","Spearfisher","Dive centre buying stock","Diver needing servicing"],
  "creator":     ["Beginner diver","Anxious diver","Dive traveller","Gear nerd","Eco-conscious diver","Future dive pro","Other instructors","Dive businesses","Brand partners"],
};
const CHANNEL_LIST = ["Instagram","Facebook","TikTok","LinkedIn","YouTube Shorts","Email newsletter","WhatsApp","Blog","Website landing pages","Google Business Profile","Google Ads","Meta Ads","Pinterest","Reddit/community","Tripadvisor/reviews","Other"];
const TONE_OPTIONS = ["Friendly","Expert","Calm","Premium","Adventurous","Playful","Technical","Minimalist","Educational","Bold","Conservation-led","Wellness-led","Luxury","Local/community-based"];
const ASSET_OPTIONS = ["Recent dive videos","Course photos","Student photos","Testimonials","Google reviews","Dive site footage","Staff photos","Gear/product images","Blog articles","Email list","Lead magnet","Brand guide","Logo","Existing ads","FAQs","Pricing sheet","Course descriptions","Website pages","Customer questions","Seasonal promotions"];

export default function StrategyOnboarding() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [access, setAccess] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [restoredFromLocal, setRestoredFromLocal] = useState(false);

  // localStorage-only autosave for mid-section typing (server persistence happens on section Save).
  const localKey = profile?.user_id ? `dms_strategy_onboarding_v1:${profile.user_id}` : null;
  useEffect(() => {
    if (!localKey || loading) return;
    const t = setTimeout(() => {
      try { localStorage.setItem(localKey, JSON.stringify({ data, stepIdx, saved_at: new Date().toISOString() })); }
      catch (e) { console.debug("[strategy-onboarding] localStorage save failed:", e?.name); }
    }, 800);
    return () => clearTimeout(t);
  }, [data, stepIdx, localKey, loading]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const acc = await api.get("/strategy/access");
      setAccess(acc.data);
      if (!acc.data.has_access) return;
      const { data: pr } = await api.get("/strategy/profile");
      setProfile(pr.profile);
      const serverData = pr.profile.onboarding || {};
      // Restore local unsaved edits if newer than server
      const key = pr.profile.user_id ? `dms_strategy_onboarding_v1:${pr.profile.user_id}` : null;
      let restored = false;
      if (key) {
        try {
          const raw = localStorage.getItem(key);
          if (raw) {
            const local = JSON.parse(raw);
            const localTs = local?.saved_at ? new Date(local.saved_at).getTime() : 0;
            const serverTs = pr.profile.updated_at ? new Date(pr.profile.updated_at).getTime() : 0;
            if (local?.data && localTs > serverTs + 500) {
              // Local has newer unsaved edits — restore them
              setData({ ...serverData, ...local.data });
              if (typeof local.stepIdx === "number") setStepIdx(local.stepIdx);
              restored = true;
              setRestoredFromLocal(true);
              toast.success("Welcome back — restored your unsaved edits.");
            }
          }
        } catch (e) { console.debug("[strategy-onboarding] restore failed:", e?.name); }
      }
      if (!restored) {
        setData(serverData);
        const done = new Set(pr.profile.onboarding_completed_sections || []);
        const nextIdx = SECTIONS.findIndex(s => !done.has(s.key));
        setStepIdx(nextIdx === -1 ? 0 : nextIdx);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const currentSection = SECTIONS[stepIdx];
  const sectionData = data[currentSection?.key];
  const doneSet = useMemo(() => new Set(profile?.onboarding_completed_sections || []), [profile]);

  const updateField = (patch) => {
    setData(prev => ({
      ...prev,
      [currentSection.key]: { ...(prev[currentSection.key] || {}), ...patch },
    }));
  };
  const setSectionValue = (value) => {
    setData(prev => ({ ...prev, [currentSection.key]: value }));
  };

  const autosave = async (opts = { markComplete: false, nextStep: false, finish: false }) => {
    if (!currentSection) return;
    const payload = {
      data: data[currentSection.key] ?? null,
      mark_complete: !!opts.markComplete,
    };
    setSaving(true);
    try {
      const { data: r } = await api.patch(`/strategy/onboarding/${currentSection.key}`, payload);
      setProfile(r.profile);
      setSavedAt(new Date());
      setRestoredFromLocal(false);
      // Any local edits are now committed to the server; clear the drift-tracker
      if (localKey) {
        try { localStorage.removeItem(localKey); } catch (e) { console.debug("[strategy-onboarding] localStorage clear failed:", e?.name); }
      }
      if (opts.nextStep && stepIdx < SECTIONS.length - 1) setStepIdx(stepIdx + 1);
      if (opts.finish) {
        setFinishing(true);
        try {
          const { data: rf } = await api.post("/strategy/onboarding/complete");
          setProfile(rf.profile);
          toast.success("Strategy onboarding complete.");
          nav("/strategy");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Could not complete onboarding.");
        } finally { setFinishing(false); }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  const goPrev = () => stepIdx > 0 && setStepIdx(stepIdx - 1);

  const isLast = stepIdx === SECTIONS.length - 1;

  if (loading) return <div className="p-10 text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>;
  if (!access?.has_access) {
    return (
      <div className="p-10 text-[#64748B]">
        <p>Strategy Operator is not enabled on this workspace.</p>
        <Link to="/strategy" className="text-[#1D4E89] underline mt-2 inline-block">Back</Link>
      </div>
    );
  }

  return (
    <div data-testid="strategy-onboarding" className="max-w-3xl px-5 sm:px-6 lg:px-10 py-8">
      <Link to="/strategy" className="text-[13.5px] text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Strategy home
      </Link>
      <div className="dms-label mb-2">Strategy onboarding</div>
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342]">{currentSection.label}</h1>
      <p className="mt-2 text-[#64748B] text-[13.5px]">{currentSection.intro}</p>

      {/* Progress bar */}
      <div className="mt-6 flex items-center gap-1.5" data-testid="strategy-step-progress">
        {SECTIONS.map((s, i) => (
          <button
            key={s.key}
            onClick={() => setStepIdx(i)}
            data-testid={`strategy-step-${s.key}`}
            title={s.label}
            className={`flex-1 h-1.5 rounded-full transition-colors ${
              i === stepIdx ? "bg-[#0A2342]" : doneSet.has(s.key) ? "bg-[#4FD1C5]" : "bg-[#E2E8F0]"
            }`}
          />
        ))}
      </div>
      <div className="mt-1 text-[11px] text-[#94A3B8]">Step {stepIdx + 1} of {SECTIONS.length}</div>

      {/* Section body */}
      <div className="mt-6 dms-card p-5 sm:p-6">
        <SectionFields
          section={currentSection.key}
          value={sectionData}
          onPatch={updateField}
          onReplace={setSectionValue}
        />
      </div>

      {/* Footer */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button onClick={goPrev} disabled={stepIdx === 0} data-testid="strategy-prev" className="dms-btn-ghost">
          <ArrowLeft className="w-4 h-4" /> Previous
        </button>
        <button onClick={() => autosave({ markComplete: false })} disabled={saving} data-testid="strategy-save" className="dms-btn-ghost">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save draft
        </button>
        {savedAt && !saving && (
          <span className="text-[11.5px] text-[#94A3B8]">
            Saved {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
        <div className="ml-auto flex gap-2">
          {!isLast ? (
            <button onClick={() => autosave({ markComplete: true, nextStep: true })} disabled={saving} data-testid="strategy-save-next" className="dms-btn-primary">
              Save & continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => autosave({ markComplete: true, finish: true })}
              disabled={saving || finishing}
              data-testid="strategy-finish"
              className="dms-btn-primary"
            >
              {finishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Complete onboarding
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- Section forms */

function SectionFields({ section, value, onPatch, onReplace }) {
  const v = value || {};
  switch (section) {
    case "business_basics":  return <BusinessBasicsForm v={v} onPatch={onPatch} />;
    case "goals":            return <GoalsForm v={v} onPatch={onPatch} />;
    case "offers":           return <OffersForm v={Array.isArray(value) ? value : []} onReplace={onReplace} />;
    case "audience_segments":return <AudienceForm v={Array.isArray(value) ? value : []} onReplace={onReplace} />;
    case "channels":         return <ChannelsForm v={Array.isArray(value) ? value : []} onReplace={onReplace} />;
    case "brand_voice":      return <BrandVoiceForm v={v} onPatch={onPatch} />;
    case "assets":           return <AssetsForm v={v} onPatch={onPatch} />;
    case "constraints":      return <ConstraintsForm v={v} onPatch={onPatch} />;
    default:                 return null;
  }
}

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <div className="text-[12.5px] font-semibold text-[#334155]">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-[#94A3B8] mt-1">{hint}</div>}
    </label>
  );
}
function Input(props) { return <input {...props} className={`dms-input mt-1 ${props.className || ""}`} />; }
function Textarea(props) { return <textarea {...props} className={`dms-input mt-1 min-h-[80px] ${props.className || ""}`} />; }
function Select(props) { return <select {...props} className={`dms-input mt-1 ${props.className || ""}`}>{props.children}</select>; }

function BusinessBasicsForm({ v, onPatch }) {
  return (
    <div className="space-y-4">
      <Field label="Business name"><Input data-testid="bb-name" value={v.business_name || ""} onChange={e => onPatch({ business_name: e.target.value })} placeholder="e.g. Blue Lagoon Divers" /></Field>
      <Field label="Website URL"><Input data-testid="bb-website" value={v.website || ""} onChange={e => onPatch({ website: e.target.value })} placeholder="https://…" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Location"><Input data-testid="bb-location" value={v.location || ""} onChange={e => onPatch({ location: e.target.value })} placeholder="City, country" /></Field>
        <Field label="Business type">
          <Select data-testid="bb-type" value={v.business_type || ""} onChange={e => onPatch({ business_type: e.target.value })}>
            <option value="">Select…</option>
            {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Certification agency">
          <Select data-testid="bb-agency" value={v.certification_agency || ""} onChange={e => onPatch({ certification_agency: e.target.value })}>
            <option value="">Select…</option>
            {CERT_AGENCIES.map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
        <Field label="Main languages"><Input data-testid="bb-languages" value={v.languages || ""} onChange={e => onPatch({ languages: e.target.value })} placeholder="e.g. English, Spanish" /></Field>
      </div>
      <Field label="Short business description"><Textarea data-testid="bb-desc" value={v.description || ""} onChange={e => onPatch({ description: e.target.value })} placeholder="A dive centre on Malta's east coast running beginner PADI courses…" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Primary CTA method">
          <Select data-testid="bb-cta-type" value={v.primary_cta_type || ""} onChange={e => onPatch({ primary_cta_type: e.target.value })}>
            <option value="">Select…</option>
            {CTA_METHODS.map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
        <Field label="CTA link or contact"><Input data-testid="bb-cta-value" value={v.primary_cta_value || ""} onChange={e => onPatch({ primary_cta_value: e.target.value })} placeholder="https://wa.me/… or email@…" /></Field>
      </div>
      <Field label="Main seasonality"><Input data-testid="bb-seasonality" value={v.seasonality || ""} onChange={e => onPatch({ seasonality: e.target.value })} placeholder="e.g. Peak May–October" /></Field>
    </div>
  );
}

function GoalsForm({ v, onPatch }) {
  const secondary = v.secondary_goals || [];
  const toggleSecondary = (g) => {
    onPatch({ secondary_goals: secondary.includes(g) ? secondary.filter(x => x !== g) : [...secondary, g] });
  };
  return (
    <div className="space-y-4">
      <Field label="Primary goal (pick one)">
        <Select data-testid="goals-primary" value={v.primary_goal || ""} onChange={e => onPatch({ primary_goal: e.target.value })}>
          <option value="">Select the single most important outcome…</option>
          {GOAL_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
        </Select>
      </Field>
      <Field label="Secondary goals (pick any)">
        <div className="mt-2 flex flex-wrap gap-1.5">
          {GOAL_OPTIONS.filter(g => g !== v.primary_goal).map(g => (
            <button
              type="button"
              key={g}
              onClick={() => toggleSecondary(g)}
              data-testid={`goals-secondary-${g.slice(0, 20)}`}
              className={`text-[12px] px-2.5 py-1 rounded-full border transition-colors ${
                secondary.includes(g)
                  ? "bg-[#0A2342] text-white border-[#0A2342]"
                  : "bg-white border-[#E2E8F0] text-[#334155] hover:border-[#1D4E89]"
              }`}
            >{g}</button>
          ))}
        </div>
      </Field>
      <Field label="Target outcome (any numbers?)"><Textarea data-testid="goals-target" value={v.target_outcome || ""} onChange={e => onPatch({ target_outcome: e.target.value })} placeholder="e.g. 50 Open Water students in Q3, or 200 new email subscribers by September." /></Field>
      <Field label="Urgency / campaign dates"><Input data-testid="goals-urgency" value={v.urgency || ""} onChange={e => onPatch({ urgency: e.target.value })} placeholder="e.g. Summer high-season starts 1 June" /></Field>
    </div>
  );
}

function OffersForm({ v, onReplace }) {
  const list = v;
  const addOffer = () => onReplace([...list, { name: "", type: "Course", price: "", currency: "EUR", priority: "Priority", description: "" }]);
  const updateOffer = (i, patch) => onReplace(list.map((o, idx) => idx === i ? { ...o, ...patch } : o));
  const removeOffer = (i) => onReplace(list.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {list.length === 0 && <div className="text-[13px] text-[#94A3B8]">Add your first offer to start the strategy.</div>}
      {list.map((o, i) => (
        <div key={i} className="dms-card p-4 !shadow-none border" data-testid={`offer-item-${i}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#64748B]">Offer #{i + 1}</div>
            <button onClick={() => removeOffer(i)} data-testid={`offer-remove-${i}`} className="text-[#B91C1C] hover:bg-[#FEF2F2] p-1 rounded"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name"><Input value={o.name || ""} onChange={e => updateOffer(i, { name: e.target.value })} placeholder="e.g. Open Water Course" data-testid={`offer-name-${i}`} /></Field>
            <Field label="Type">
              <Select value={o.type || ""} onChange={e => updateOffer(i, { type: e.target.value })} data-testid={`offer-type-${i}`}>
                {OFFER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
            </Field>
            <Field label="Price"><Input type="text" value={o.price || ""} onChange={e => updateOffer(i, { price: e.target.value })} placeholder="e.g. 450" data-testid={`offer-price-${i}`} /></Field>
            <Field label="Currency"><Input value={o.currency || ""} onChange={e => updateOffer(i, { currency: e.target.value })} placeholder="EUR" data-testid={`offer-currency-${i}`} /></Field>
            <Field label="Priority">
              <Select value={o.priority || ""} onChange={e => updateOffer(i, { priority: e.target.value })} data-testid={`offer-priority-${i}`}>
                {OFFER_PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </Select>
            </Field>
            <Field label="Seasonality"><Input value={o.seasonality || ""} onChange={e => updateOffer(i, { seasonality: e.target.value })} placeholder="e.g. May–October" data-testid={`offer-seasonality-${i}`} /></Field>
          </div>
          <Field label="Description"><Textarea value={o.description || ""} onChange={e => updateOffer(i, { description: e.target.value })} placeholder="What's included, who it's for, main hook…" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <Field label="Main objections"><Textarea value={o.objections || ""} onChange={e => updateOffer(i, { objections: e.target.value })} placeholder="e.g. 'I'm scared', 'Too expensive'" /></Field>
            <Field label="Proof / testimonials"><Textarea value={o.proof || ""} onChange={e => updateOffer(i, { proof: e.target.value })} placeholder="e.g. 'Rated 4.9 on Tripadvisor'" /></Field>
          </div>
          <Field label="CTA link / booking method"><Input value={o.cta || ""} onChange={e => updateOffer(i, { cta: e.target.value })} placeholder="https://…" /></Field>
        </div>
      ))}
      <button onClick={addOffer} data-testid="offer-add" className="dms-btn-ghost"><Plus className="w-4 h-4" /> Add offer</button>
    </div>
  );
}

function AudienceForm({ v, onReplace }) {
  const [suggType, setSuggType] = useState("dive_centre");
  const suggestions = AUDIENCE_SUGGESTIONS[suggType] || [];
  const addSegment = (name) => {
    if (v.some(s => s.name === name)) return;
    onReplace([...v, { name, description: "", pain_points: "", desires: "", objections: "", best_channels: "", best_angles: "" }]);
  };
  const updateSeg = (i, patch) => onReplace(v.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const removeSeg = (i) => onReplace(v.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[12.5px] font-semibold text-[#334155]">Suggested audience segments</div>
        <div className="mt-2 flex flex-wrap gap-1.5 text-[12px]">
          {["dive_centre","gear_shop","creator"].map(k => (
            <button key={k} onClick={() => setSuggType(k)} className={`px-2.5 py-1 rounded-md border ${suggType === k ? "bg-[#0A2342] text-white border-[#0A2342]" : "bg-white border-[#E2E8F0] text-[#334155]"}`} data-testid={`aud-sugg-${k}`}>
              {k === "dive_centre" ? "Dive centre" : k === "gear_shop" ? "Gear shop" : "Creator"}
            </button>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {suggestions.map(s => (
            <button key={s} onClick={() => addSegment(s)} className="text-[12px] px-2.5 py-1 rounded-full border border-[#E2E8F0] hover:border-[#1D4E89] bg-white" data-testid={`aud-add-${s.slice(0,20)}`}>+ {s}</button>
          ))}
        </div>
      </div>
      {v.length === 0 && <div className="text-[13px] text-[#94A3B8]">Add at least one audience segment.</div>}
      {v.map((s, i) => (
        <div key={i} className="dms-card p-4 !shadow-none border" data-testid={`aud-item-${i}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-bold text-[#0A2342] text-[13.5px]">{s.name || `Segment #${i+1}`}</div>
            <button onClick={() => removeSeg(i)} className="text-[#B91C1C] hover:bg-[#FEF2F2] p-1 rounded"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name"><Input value={s.name || ""} onChange={e => updateSeg(i, { name: e.target.value })} /></Field>
            <Field label="Best channels"><Input value={s.best_channels || ""} onChange={e => updateSeg(i, { best_channels: e.target.value })} placeholder="Instagram, Facebook, email…" /></Field>
          </div>
          <Field label="Description / who they are"><Textarea value={s.description || ""} onChange={e => updateSeg(i, { description: e.target.value })} /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            <Field label="Pain points"><Textarea value={s.pain_points || ""} onChange={e => updateSeg(i, { pain_points: e.target.value })} /></Field>
            <Field label="Desires"><Textarea value={s.desires || ""} onChange={e => updateSeg(i, { desires: e.target.value })} /></Field>
            <Field label="Objections"><Textarea value={s.objections || ""} onChange={e => updateSeg(i, { objections: e.target.value })} /></Field>
          </div>
        </div>
      ))}
      <button onClick={() => addSegment(`Segment ${v.length + 1}`)} className="dms-btn-ghost" data-testid="aud-add-custom"><Plus className="w-4 h-4" /> Add custom segment</button>
    </div>
  );
}

function ChannelsForm({ v, onReplace }) {
  const toggle = (name) => {
    if (v.some(c => c.name === name)) {
      onReplace(v.filter(c => c.name !== name));
    } else {
      onReplace([...v, { name, status: "active", posting_frequency: "", include_in_strategy: true, profile_url: "", audience_size: "", confidence: "medium", purpose: "" }]);
    }
  };
  const update = (name, patch) => onReplace(v.map(c => c.name === name ? { ...c, ...patch } : c));
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[12.5px] font-semibold text-[#334155] mb-2">Select the channels you actually use (or plan to)</div>
        <div className="flex flex-wrap gap-1.5">
          {CHANNEL_LIST.map(ch => {
            const active = v.some(c => c.name === ch);
            return (
              <button key={ch} onClick={() => toggle(ch)} data-testid={`chn-toggle-${ch.slice(0, 15)}`}
                className={`text-[12px] px-2.5 py-1 rounded-full border ${active ? "bg-[#0A2342] text-white border-[#0A2342]" : "bg-white border-[#E2E8F0] text-[#334155]"}`}>{ch}</button>
            );
          })}
        </div>
      </div>
      {v.map((c, i) => (
        <div key={c.name} className="dms-card p-4 !shadow-none border" data-testid={`chn-item-${i}`}>
          <div className="font-heading font-bold text-[#0A2342] text-[13.5px]">{c.name}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            <Field label="Status">
              <Select value={c.status} onChange={e => update(c.name, { status: e.target.value })}>
                <option value="active">Active</option><option value="planned">Planned</option><option value="inactive">Inactive</option>
              </Select>
            </Field>
            <Field label="Posting frequency"><Input value={c.posting_frequency || ""} onChange={e => update(c.name, { posting_frequency: e.target.value })} placeholder="e.g. 3× per week" /></Field>
            <Field label="Audience size"><Input value={c.audience_size || ""} onChange={e => update(c.name, { audience_size: e.target.value })} placeholder="e.g. 4,200 followers" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <Field label="Profile URL"><Input value={c.profile_url || ""} onChange={e => update(c.name, { profile_url: e.target.value })} placeholder="https://…" /></Field>
            <Field label="Strategic role"><Input value={c.purpose || ""} onChange={e => update(c.name, { purpose: e.target.value })} placeholder="e.g. Local awareness, conversion, authority…" /></Field>
          </div>
          <label className="mt-3 inline-flex items-center gap-2 text-[12.5px] text-[#334155]">
            <input type="checkbox" checked={c.include_in_strategy !== false} onChange={e => update(c.name, { include_in_strategy: e.target.checked })} /> Include in 90-day strategy
          </label>
        </div>
      ))}
    </div>
  );
}

function BrandVoiceForm({ v, onPatch }) {
  const tones = v.tone || [];
  const toggleTone = (t) => onPatch({ tone: tones.includes(t) ? tones.filter(x => x !== t) : [...tones, t] });
  return (
    <div className="space-y-4">
      <Field label="Tone (pick 2–4 that fit your brand best)">
        <div className="mt-2 flex flex-wrap gap-1.5">
          {TONE_OPTIONS.map(t => (
            <button type="button" key={t} onClick={() => toggleTone(t)} data-testid={`tone-${t}`}
              className={`text-[12px] px-2.5 py-1 rounded-full border ${tones.includes(t) ? "bg-[#0A2342] text-white border-[#0A2342]" : "bg-white border-[#E2E8F0] text-[#334155]"}`}>{t}</button>
          ))}
        </div>
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Words / phrases you like"><Textarea data-testid="bv-vocab" value={v.vocabulary || ""} onChange={e => onPatch({ vocabulary: e.target.value })} placeholder="Words your brand uses often" /></Field>
        <Field label="Words / phrases to avoid"><Textarea data-testid="bv-avoid" value={v.words_to_avoid || ""} onChange={e => onPatch({ words_to_avoid: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Emoji preference">
          <Select data-testid="bv-emoji" value={v.emoji_preference || ""} onChange={e => onPatch({ emoji_preference: e.target.value })}>
            <option value="">Pick…</option><option value="none">None</option><option value="light">Light</option><option value="heavy">Heavy</option>
          </Select>
        </Field>
        <Field label="Formality">
          <Select data-testid="bv-formality" value={v.formality || ""} onChange={e => onPatch({ formality: e.target.value })}>
            <option value="">Pick…</option><option value="casual">Casual</option><option value="balanced">Balanced</option><option value="formal">Formal</option>
          </Select>
        </Field>
      </div>
      <Field label="Safety / compliance notes"><Textarea data-testid="bv-safety" value={v.safety_notes || ""} onChange={e => onPatch({ safety_notes: e.target.value })} placeholder="Any agency-specific language rules or safety claims to always/never make." /></Field>
    </div>
  );
}

function AssetsForm({ v, onPatch }) {
  const have = v.have || [];
  const toggle = (a) => onPatch({ have: have.includes(a) ? have.filter(x => x !== a) : [...have, a] });
  return (
    <div className="space-y-4">
      <div className="text-[13px] text-[#64748B]">Tick everything you already have. Anything unticked will drive DMS to generate <b>capture tasks</b> instead of finished content.</div>
      <div className="flex flex-wrap gap-1.5">
        {ASSET_OPTIONS.map(a => (
          <button type="button" key={a} onClick={() => toggle(a)} data-testid={`asset-${a.slice(0, 15)}`}
            className={`text-[12px] px-2.5 py-1 rounded-full border ${have.includes(a) ? "bg-[#0A2342] text-white border-[#0A2342]" : "bg-white border-[#E2E8F0] text-[#334155]"}`}>{a}</button>
        ))}
      </div>
      <Field label="Notes / anything else DMS should know about your assets"><Textarea data-testid="assets-notes" value={v.notes || ""} onChange={e => onPatch({ notes: e.target.value })} /></Field>
    </div>
  );
}

function ConstraintsForm({ v, onPatch }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Posting days per week"><Input type="number" min="0" max="7" data-testid="c-post-days" value={v.post_days_per_week ?? ""} onChange={e => onPatch({ post_days_per_week: parseInt(e.target.value || 0) })} /></Field>
        <Field label="Hours you can spend per week"><Input type="number" min="0" data-testid="c-hours" value={v.hours_per_week ?? ""} onChange={e => onPatch({ hours_per_week: parseInt(e.target.value || 0) })} /></Field>
        <Field label="Preferred reminder time"><Input type="time" data-testid="c-reminder-time" value={v.reminder_time || ""} onChange={e => onPatch({ reminder_time: e.target.value })} /></Field>
      </div>
      <Field label="Time zone"><Input data-testid="c-tz" value={v.timezone || ""} onChange={e => onPatch({ timezone: e.target.value })} placeholder="Europe/Malta, America/New_York…" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Channels you refuse to use"><Textarea data-testid="c-avoid-channels" value={v.avoid_channels || ""} onChange={e => onPatch({ avoid_channels: e.target.value })} placeholder="e.g. TikTok" /></Field>
        <Field label="Topics you refuse to cover"><Textarea data-testid="c-avoid-topics" value={v.avoid_topics || ""} onChange={e => onPatch({ avoid_topics: e.target.value })} placeholder="e.g. Solo diving beyond training" /></Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-[12.5px] flex items-start gap-2"><input type="checkbox" data-testid="c-batch" checked={!!v.batch_weekly} onChange={e => onPatch({ batch_weekly: e.target.checked })} /> I prefer to batch content weekly, not daily</label>
        <label className="text-[12.5px] flex items-start gap-2"><input type="checkbox" data-testid="c-require-approval" checked={!!v.require_approval} onChange={e => onPatch({ require_approval: e.target.checked })} /> Require my approval for every content item</label>
        <label className="text-[12.5px] flex items-start gap-2"><input type="checkbox" data-testid="c-daily-reminder" checked={v.daily_reminder !== false} onChange={e => onPatch({ daily_reminder: e.target.checked })} /> Send me a daily reminder (even when content is pre-approved)</label>
      </div>
    </div>
  );
}
